// src/pages/EmployeeDetailPage.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  arrayUnion,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { useAuthorizedAccess } from '../hooks/useAuthorizedAccess';
import { 
  FaArrowLeft, 
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaBriefcase,
  FaCalendarAlt,
  FaIdCard,
  FaUniversity,
  FaCreditCard,
  FaFileUpload,
  FaFileAlt,
  FaLink,
  FaEdit,
  FaStickyNote,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaEye,
  FaTrash,
  FaCertificate,
  FaTrophy,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import AppraisalModal from '../components/AppraisalModal';
import ChecklistTemplateManager from './ChecklistTemplateManager';
import './EmployeeDetailPage.css';

const EmployeeDetailPage = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { hasAccess, loading: accessLoading } = useAuthorizedAccess();

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('basic'); // basic, documents, drives, notes, salary, experience
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [selectedDriveType, setSelectedDriveType] = useState('');
  const [driveLink, setDriveLink] = useState('');
  const [driveLinkName, setDriveLinkName] = useState(''); // Add link name/description
  const [newNote, setNewNote] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);
  const [savingDriveLink, setSavingDriveLink] = useState(false);
  const [deletingDriveLink, setDeletingDriveLink] = useState(null);
  const [appraisalOpen, setAppraisalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const profilePicInputRef = useRef(null);

  // Document categories: driven by the viewed employee's DB role (superadmin's
  // own record holds company-legal docs) — scales to any superadmin, no hardcoded email/ID.
  const isSuperadmin = employee?.role === 'superadmin';
  // Show appraisal/promotion actions only when admin/superadmin is viewing another employee's record
  const canAppraise =
    (userData?.role === 'superadmin' || userData?.role === 'admin') &&
    employee?.id !== userData?.uid &&
    employee?.employeeId !== userData?.employeeId;
  const documentTypes = isSuperadmin
    ? [
        
        { key: 'gstCertification', label: 'GST Certification' },
        { key: 'certificateOfIncorporation', label: 'Certificate of Incorporation' },
        { key: 'prrepxPathshala9001', label: 'PRREPX PATHSHALA PRIVATE LIMITED 9001(IS...)' },
        { key: 'esic', label: 'ESIC' },
        { key: 'moa', label: 'MOA' },
        { key: 'aoa', label: 'AOA' },
        { key: 'directors', label: 'Directors Docs' },
        { key: 'compliance', label: 'Compliance Related' },
        { key: 'others', label: 'Others' }
      ]
    : [
        { key: 'offerLetter', label: 'Offer Letter' },
        { key: 'contract', label: 'Employment Contract' },
        { key: 'idProof', label: 'ID Proof (Aadhar/PAN)' },
        { key: 'educationCertificates', label: 'Education Certificates' },
        { key: 'bankDocuments', label: 'Bank Documents' },
        { key: 'other', label: 'Other Documents' }
      ];

  // Drive link types
  const driveLinkTypes = isSuperadmin
    ? [
        { key: 'companyFolder', label: 'Company Folder' },
        { key: 'cofoundersFolder', label: 'Cofounders Folder' },
        { key: 'legalFolder', label: 'Legal Documents Folder' },
        { key: 'sensitiveFolder', label: 'Sensitive Docs Folder' },
        { key: 'other', label: 'Other' }
      ]
    : [
        { key: 'personalFolder', label: 'Personal Folder' },
        { key: 'projectFolder', label: 'Project Folder' },
        { key: 'notesFolder', label: 'Notes Folder' },
        { key: 'recordingsFolder', label: 'Recordings Folder' },
        { key: 'documentsFolder', label: 'Documents Folder' },
        { key: 'custom', label: 'Custom Link' }
      ];

  // Fetch employee data — single source of truth is Firestore; re-callable after any save
  const fetchEmployee = useCallback(async () => {
    try {
      setLoading(true);
      const empDoc = await getDoc(doc(db, 'employee_records_complete', employeeId));

      if (empDoc.exists()) {
        setEmployee({ id: empDoc.id, ...empDoc.data() });
      } else {
        toast.error('Employee not found');
        navigate('/dashboard/employee-list');
      }
    } catch (error) {
      console.error('Error fetching employee:', error);
      toast.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  }, [employeeId, navigate]);

  useEffect(() => {
    if (employeeId) {
      fetchEmployee();
    }
  }, [employeeId, fetchEmployee]);

  // Check access
  if (accessLoading) {
    return <div className="loading-container"><FaSpinner className="spinner" /> Checking access...</div>;
  }

  if (!hasAccess) {
    return (
      <div className="access-denied-container">
        <FaTimesCircle className="access-denied-icon" />
        <h2>Access Denied</h2>
        <p>You don't have permission to view employee details.</p>
        <button onClick={() => navigate('/dashboard')} className="btn-primary">
          Go to Dashboard
        </button>
      </div>
    );
  }

  // Handle document upload (supports multiple files)
  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) {
      console.log('❌ No files selected');
      return;
    }
    
    if (!selectedDocType) {
      toast.error('Please select a document type first');
      e.target.value = '';
      return;
    }

    if (uploading) {
      console.log('❌ Upload already in progress');
      return;
    }

    console.log('=== STARTING UPLOAD ===');
    console.log('Files to upload:', files.length);
    console.log('Type:', selectedDocType);
    console.log('Employee ID:', employeeId);
    console.log('User:', userData?.name, userData?.uid);

    setUploading(true);
    setUploadProgress(0);

    const totalFiles = files.length;
    let uploadedCount = 0;

    // Get current documents ONCE outside the loop
    let currentDocs = (employee?.documents && typeof employee.documents === 'object' && !Array.isArray(employee.documents)) 
      ? { ...employee.documents } 
      : {};
    
    // Initialize array if doesn't exist, or convert old single object to array
    if (!currentDocs[selectedDocType]) {
      currentDocs[selectedDocType] = [];
    } else if (!Array.isArray(currentDocs[selectedDocType])) {
      // Convert old format (single object) to new format (array)
      currentDocs[selectedDocType] = [currentDocs[selectedDocType]];
    }

    try {
      // Upload files sequentially
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`\n📄 Uploading file ${i + 1}/${totalFiles}: ${file.name}`);

        const timestamp = Date.now() + i; // Unique timestamp for each file
        const fileName = `${selectedDocType}_${timestamp}_${file.name}`;
        const storageRef = ref(storage, `employee_documents/${employeeId}/${fileName}`);
        
        console.log('📤 Storage path:', `employee_documents/${employeeId}/${fileName}`);
        
        // Use uploadBytesResumable for progress tracking
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        // Create a promise to handle the upload
        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            // Progress function
            (snapshot) => {
              const fileProgress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              // Calculate overall progress: (completed files + current file progress) / total files
              const overallProgress = Math.round(((uploadedCount + (fileProgress / 100)) / totalFiles) * 100);
              setUploadProgress(overallProgress);
              console.log(`📊 File ${i + 1}/${totalFiles} progress: ${fileProgress}% | Overall: ${overallProgress}%`);
            },
          // Error function
          (error) => {
            console.error('❌ Upload error:', error);
            console.error('Error code:', error.code);
            console.error('Error message:', error.message);
            reject(error);
          },
          // Complete function
          async () => {
            console.log('✅ Upload to storage complete!');
            try {
              console.log('🔗 Getting download URL...');
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log('✅ Download URL obtained:', downloadURL);
              resolve(downloadURL);
            } catch (urlError) {
              console.error('❌ Error getting download URL:', urlError);
              reject(urlError);
            }
          }
        );
        }).then(async (downloadURL) => {
          // Prepare document data
          const newDocData = {
            url: downloadURL,
            fileName: file.name,
            uploadedAt: new Date().toISOString(),
            uploadedBy: userData?.uid || 'system',
            uploaderName: userData?.name || 'System',
            id: `${selectedDocType}_${timestamp}_${i}` // Unique ID for deletion
          };
          
          // Add new document to the local array
          currentDocs[selectedDocType] = [...currentDocs[selectedDocType], newDocData];

          console.log('💾 Updating Firestore...');
          console.log('Document data:', newDocData);
          console.log('Total docs in this category:', currentDocs[selectedDocType].length);
          
          await updateDoc(doc(db, 'employee_records_complete', employeeId), {
            documents: currentDocs,
            updatedAt: new Date().toISOString()
          });

          console.log(`✅ File ${i + 1}/${totalFiles} uploaded successfully!`);

          uploadedCount++;
        });
      }
      
      // Update local state ONCE after all uploads
      setEmployee(prev => ({
        ...prev,
        documents: currentDocs
      }));
      
      // All files uploaded successfully
      toast.success(`✅ ${totalFiles} document${totalFiles > 1 ? 's' : ''} uploaded successfully!`);
      setSelectedDocType('');
      e.target.value = '';
      
    } catch (error) {
      console.error('❌❌❌ UPLOAD FAILED ❌❌❌');
      console.error('Error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      toast.error(`Upload failed: ${error.message || 'Unknown error'}`);
      e.target.value = '';
    } finally {
      setUploading(false);
      setUploadProgress(0);
      console.log('=== UPLOAD PROCESS ENDED ===');
    }
  };

  // Handle drive link save
  const handleSaveDriveLink = async () => {
    if (!selectedDriveType || !driveLink) {
      toast.error('Please enter a valid drive link');
      return;
    }

    if (!driveLinkName.trim()) {
      toast.error('Please enter a name/description for the link');
      return;
    }

    setSavingDriveLink(true);
    try {
      // Ensure driveLinks is an object, not an array (for old records)
      const currentDriveLinks = employee.driveLinks && typeof employee.driveLinks === 'object' && !Array.isArray(employee.driveLinks) 
        ? { ...employee.driveLinks }
        : {};
      
      // Initialize array if doesn't exist, or convert old single string to array
      if (!currentDriveLinks[selectedDriveType]) {
        currentDriveLinks[selectedDriveType] = [];
      } else if (!Array.isArray(currentDriveLinks[selectedDriveType])) {
        // Convert old format (single string) to new format (array of objects)
        currentDriveLinks[selectedDriveType] = [{
          url: currentDriveLinks[selectedDriveType],
          name: 'Drive Link',
          id: `${selectedDriveType}_${Date.now()}`
        }];
      }
      
      // Add new link to array
      const newLinkData = {
        url: driveLink,
        name: driveLinkName.trim(),
        addedAt: new Date().toISOString(),
        addedBy: userData?.uid || 'system',
        id: `${selectedDriveType}_${Date.now()}`
      };
      
      currentDriveLinks[selectedDriveType] = [...currentDriveLinks[selectedDriveType], newLinkData];

      const updateData = {
        driveLinks: currentDriveLinks,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'employee_records_complete', employeeId), updateData);

      setEmployee(prev => ({
        ...prev,
        driveLinks: currentDriveLinks
      }));

      toast.success('Drive link saved successfully!');
      setSelectedDriveType('');
      setDriveLink('');
      setDriveLinkName('');
    } catch (error) {
      console.error('Error saving drive link:', error);
      toast.error('Failed to save drive link');
    } finally {
      setSavingDriveLink(false);
    }
  };

  // Handle drive link delete (superadmin only)
  const handleDeleteDriveLink = async (linkType, linkId) => {
    console.log('🗑️ Delete drive link clicked:', { linkType, linkId, role: userData?.role });
    
    if (userData?.role !== 'superadmin') {
      toast.error('Only superadmin can delete drive links');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this link?')) {
      return;
    }

    setDeletingDriveLink(linkId);
    try {
      console.log('Current employee driveLinks:', employee.driveLinks);
      
      // Create a deep copy of driveLinks to ensure React detects the change
      const currentDriveLinks = {};
      Object.keys(employee.driveLinks || {}).forEach(key => {
        currentDriveLinks[key] = [...(employee.driveLinks[key] || [])];
      });
      
      console.log('Current drive links for type:', linkType, currentDriveLinks[linkType]);
      
      if (Array.isArray(currentDriveLinks[linkType])) {
        console.log('Before delete:', currentDriveLinks[linkType].length, 'links');
        // Filter out the link with matching ID
        currentDriveLinks[linkType] = currentDriveLinks[linkType].filter(link => link.id !== linkId);
        console.log('After delete:', currentDriveLinks[linkType].length, 'links');
        console.log('Filtered array:', currentDriveLinks[linkType]);
        
        // Remove the category if empty
        if (currentDriveLinks[linkType].length === 0) {
          delete currentDriveLinks[linkType];
          console.log('Deleted empty category');
        }
      }

      console.log('Updating Firestore with:', currentDriveLinks);
      await updateDoc(doc(db, 'employee_records_complete', employeeId), {
        driveLinks: currentDriveLinks,
        updatedAt: new Date().toISOString()
      });

      console.log('Updating local state...');
      setEmployee(prev => {
        const updated = {
          ...prev,
          driveLinks: currentDriveLinks
        };
        console.log('New state will be:', updated);
        return updated;
      });

      toast.success('Drive link deleted successfully');
      console.log('✅ Delete completed successfully');
    } catch (error) {
      console.error('❌ Error deleting drive link:', error);
      toast.error('Failed to delete drive link: ' + error.message);
    } finally {
      setDeletingDriveLink(null);
    }
  };

  // Handle document delete (superadmin only)
  const handleDeleteDocument = async (docType, docId) => {
    if (userData?.role !== 'superadmin') {
      toast.error('Only superadmin can delete documents');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const currentDocs = { ...employee.documents };
      
      if (Array.isArray(currentDocs[docType])) {
        currentDocs[docType] = currentDocs[docType].filter(doc => doc.id !== docId);
        
        if (currentDocs[docType].length === 0) {
          delete currentDocs[docType];
        }
      }

      await updateDoc(doc(db, 'employee_records_complete', employeeId), {
        documents: currentDocs,
        updatedAt: new Date().toISOString()
      });

      setEmployee(prev => ({
        ...prev,
        documents: currentDocs
      }));

      toast.success('Document deleted successfully');
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  // Handle profile picture upload
  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingProfilePic(true);

    try {
      const storageRef = ref(storage, `profile_pictures/${employeeId}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          null,
          (error) => reject(error),
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          }
        );
      }).then(async (downloadURL) => {
        // Update both collections
        await updateDoc(doc(db, 'employee_records_complete', employeeId), {
          photoURL: downloadURL,
          updatedAt: new Date().toISOString()
        });

        // Update users collection if uid exists
        if (employee.uid) {
          await updateDoc(doc(db, 'users', employee.uid), {
            photoURL: downloadURL
          });
        }

        setEmployee(prev => ({
          ...prev,
          photoURL: downloadURL
        }));

        toast.success('Profile picture updated successfully!');
      });

    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error('Failed to upload profile picture');
    } finally {
      setUploadingProfilePic(false);
      e.target.value = '';
    }
  };

  // Handle add note
  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    try {
      const noteObj = {
        text: newNote,
        addedAt: new Date().toISOString(),
        addedBy: userData.uid,
        addedByName: userData.name
      };

      await updateDoc(doc(db, 'employee_records_complete', employeeId), {
        notes: arrayUnion(noteObj),
        updatedAt: serverTimestamp()
      });

      setEmployee(prev => ({
        ...prev,
        notes: [...(prev.notes || []), noteObj]
      }));

      toast.success('Note added successfully!');
      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    }
  };

  // Handle field edit
  const handleEditField = async (fieldPath, value) => {
    try {
      await updateDoc(doc(db, 'employee_records_complete', employeeId), {
        [fieldPath]: value,
        updatedAt: serverTimestamp()
      });

      setEmployee(prev => {
        const keys = fieldPath.split('.');
        const newEmployee = { ...prev };
        let current = newEmployee;
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return newEmployee;
      });

      toast.success('Field updated successfully!');
      setEditingField(null);
      setEditValue('');
    } catch (error) {
      console.error('Error updating field:', error);
      toast.error('Failed to update field');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <FaSpinner className="spinner" />
        <p>Loading employee details...</p>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="not-found-container">
        <h2>Employee Not Found</h2>
        <button onClick={() => navigate('/dashboard/employee-list')} className="btn-primary">
          Back to Employee List
        </button>
      </div>
    );
  }

  return (
    <>
    <div className="employee-detail-page">
      {/* Header */}
      <div className="detail-header">
        <button className="back-btn" onClick={() => navigate('/dashboard/employee-list')}>
          <FaArrowLeft /> Back to List
        </button>
        
        <div className="employee-header-info">
          <div className="employee-avatar-large" style={{ position: 'relative' }}>
            <img 
              src={employee.photoURL || '/default-avatar.png'} 
              alt={employee.name}
              onError={(e) => e.target.src = '/default-avatar.png'}
            />
            {(userData?.role === 'superadmin' || userData?.role === 'admin') && (
              <>
                <input 
                  ref={profilePicInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => profilePicInputRef.current?.click()}
                  disabled={uploadingProfilePic}
                  style={{
                    position: 'absolute',
                    bottom: '5px',
                    right: '5px',
                    width: '35px',
                    height: '35px',
                    borderRadius: '50%',
                    background: uploadingProfilePic ? '#999' : '#2196f3',
                    border: '2px solid white',
                    color: 'white',
                    cursor: uploadingProfilePic ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
                  }}
                  title={uploadingProfilePic ? 'Uploading...' : 'Change profile picture'}
                >
                  {uploadingProfilePic ? <FaSpinner className="spin" /> : <FaEdit />}
                </button>
              </>
            )}
          </div>
          <div className="employee-header-text">
            <h1>{employee.name}</h1>
            <div className="employee-meta">
              <span className="badge badge-id">{employee.employeeId}</span>
              <span className={`badge badge-status ${employee.status}`}>
                {employee.status === 'active' ? <FaCheckCircle /> : <FaTimesCircle />}
                {employee.status || 'Active'}
              </span>
              <span className="badge badge-role">{employee.role}</span>
            </div>
            <div className="employee-quick-info">
              <span><FaBriefcase /> {employee.position || 'N/A'}</span>
              <span><FaMapMarkerAlt /> {employee.department || 'N/A'}</span>
              <span><FaCalendarAlt /> Joined: {employee.joiningDate || 'N/A'}</span>
              <span><FaUser /> Reports to: <strong>{employee.reportingManager || 'Hanumat Lal Vishwakarma (Founder & Managing Director)'}</strong></span>
            </div>
          </div>

          {canAppraise && (
            <button
              className="btn-primary"
              style={{ alignSelf: 'flex-start', marginLeft: 'auto' }}
              onClick={() => setAppraisalOpen(true)}
              title="Record salary appraisal or role change for this employee"
            >
              <FaTrophy /> Appraise
            </button>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="tabs-navigation">
        <button 
          className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          <FaUser /> Basic Info
        </button>
        <button 
          className={`tab-btn ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <FaFileAlt /> Documents
        </button>
        <button 
          className={`tab-btn ${activeTab === 'drives' ? 'active' : ''}`}
          onClick={() => setActiveTab('drives')}
        >
          <FaLink /> Drive Links
        </button>
        <button 
          className={`tab-btn ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          <FaStickyNote /> Notes
        </button>
        <button 
          className={`tab-btn ${activeTab === 'salary' ? 'active' : ''}`}
          onClick={() => setActiveTab('salary')}
        >
          <FaCreditCard /> Salary Slip
        </button>
        <button 
          className={`tab-btn ${activeTab === 'experience' ? 'active' : ''}`}
          onClick={() => setActiveTab('experience')}
        >
          <FaCertificate /> Experience Letter
        </button>
        <button 
          className={`tab-btn ${activeTab === 'checklist-manager' ? 'active' : ''}`}
          onClick={() => setActiveTab('checklist-manager')}
          title="Manage onboarding checklist templates"
        >
          ✓ Checklist Manager
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="tab-panel">
            <div className="info-grid">
              {/* Personal Information */}
              <div className="info-card">
                <h3><FaUser /> Personal Information</h3>
                <div className="info-row">
                  <label><FaEnvelope /> Email</label>
                  <span>{employee.email || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <label><FaPhone /> Phone</label>
                  <span>{employee.phone || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <label><FaMapMarkerAlt /> Location</label>
                  <span>{employee.location || 'N/A'}</span>
                </div>
              </div>

              {/* Employment Details */}
              <div className="info-card">
                <h3><FaBriefcase /> Employment Details</h3>
                <div className="info-row">
                  <label>Position</label>
                  <span>{employee.position || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <label>Department</label>
                  <span>{employee.department || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <label>Role</label>
                  <span className={`badge badge-role`}>{employee.role || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <label>Joining Date</label>
                  <span>{employee.joiningDate || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <label>Reporting Manager</label>
                  <span>{employee.reportingManager || 'Hanumat Lal Vishwakarma (Founder & Managing Director)'}</span>
                </div>
                {employee.leavingDate && (
                  <div className="info-row">
                    <label>Leaving Date</label>
                    <span>{employee.leavingDate}</span>
                  </div>
                )}
              </div>

              {/* Bank Details */}
              <div className="info-card">
                <h3><FaUniversity /> Bank Details</h3>
                <div className="info-row">
                  <label>Bank Name</label>
                  <span>{employee.bankName || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <label>Account Number</label>
                  <span>{employee.bankAccountNumber || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <label>IFSC Code</label>
                  <span>{employee.ifscCode || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <label>Gross Monthly Salary</label>
                  <span>₹{employee.grossMonthlySalary || 'N/A'}</span>
                </div>
              </div>

              {/* Statutory Details */}
              <div className="info-card">
                <h3><FaIdCard /> Statutory Details</h3>
                <div className="info-row">
                  <label>PAN Number</label>
                  <span>{employee.panNumber || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <label>PF Number</label>
                  <span>{employee.pfNumber || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <label>UAN Number</label>
                  <span>{employee.uanNumber || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="tab-panel">
            <div className="documents-section">
              <div className="upload-section">
                <h3><FaFileUpload /> Upload Documents</h3>
                <div className="upload-controls">
                  <select 
                    value={selectedDocType} 
                    onChange={(e) => setSelectedDocType(e.target.value)}
                    className="doc-type-select"
                    disabled={uploading}
                  >
                    <option value="">Select Document Type</option>
                    {documentTypes.map(type => (
                      <option key={type.key} value={type.key}>{type.label}</option>
                    ))}
                  </select>
                  
                  <input 
                    ref={fileInputRef}
                    type="file" 
                    onChange={handleDocumentUpload}
                    disabled={!selectedDocType || uploading}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    style={{ display: 'none' }}
                    id="file-upload-input"
                    multiple
                  />
                  
                  <button 
                    type="button"
                    className="upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!selectedDocType || uploading}
                    style={{ opacity: uploading ? 0.6 : 1 }}
                  >
                    <FaFileUpload /> Choose Files
                  </button>
                  
                  {uploading && (
                    <div style={{ 
                      padding: '12px', 
                      background: '#e3f2fd', 
                      borderRadius: '8px',
                      border: '1px solid #90caf9'
                    }}>
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1976d2'
                      }}>
                        <span>📤 Uploading...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div style={{ 
                        width: '100%', 
                        height: '10px', 
                        background: '#e0e0e0', 
                        borderRadius: '5px',
                        overflow: 'hidden',
                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ 
                          width: `${uploadProgress}%`, 
                          height: '100%', 
                          background: uploadProgress === 100 ? 'linear-gradient(90deg, #4caf50, #45a049)' : 'linear-gradient(90deg, #2196f3, #1976d2)',
                          transition: 'width 0.2s ease, background 0.3s ease',
                          borderRadius: '5px'
                        }}></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="documents-grid">
                {documentTypes.map(type => {
                  const docs = employee?.documents?.[type.key];
                  const docsArray = Array.isArray(docs) ? docs : (docs ? [docs] : []); // Handle both old and new format
                  
                  return (
                    <div key={type.key} className="document-card">
                      <div className="document-header">
                        <FaFileAlt className="doc-icon" />
                        <h4>{type.label}</h4>
                        {docsArray.length > 0 && (
                          <span className="doc-count">{docsArray.length}</span>
                        )}
                      </div>
                      {docsArray.length > 0 ? (
                        <div className="documents-list">
                          {docsArray.map((doc, index) => (
                            <div key={doc.id || index} className="document-item">
                              <div className="document-info-row">
                                <div className="doc-filename-row">
                                  <FaFileAlt style={{ fontSize: '16px', color: '#2196f3', flexShrink: 0 }} />
                                  <span className="doc-name" title={doc.fileName}>{doc.fileName}</span>
                                </div>
                                <div className="document-actions-inline">
                                  <a 
                                    href={doc.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="btn-icon"
                                    title="View/Download"
                                  >
                                    <FaEye />
                                  </a>
                                  {userData?.role === 'superadmin' && (
                                    <button 
                                      onClick={() => handleDeleteDocument(type.key, doc.id)}
                                      className="btn-icon btn-icon-delete"
                                      title="Delete document"
                                    >
                                      <FaTrash />
                                    </button>
                                  )}
                                </div>
                              </div>
                              <p className="doc-meta-small">
                                Uploaded by {doc.uploaderName || 'Unknown'} • {doc.uploadedAt && (
                                  <>{typeof doc.uploadedAt === 'string' ? new Date(doc.uploadedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : doc.uploadedAt?.toDate?.()?.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) || 'Unknown date'}</>
                                )}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-document">No documents uploaded</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Drive Links Tab */}
        {activeTab === 'drives' && (
          <div className="tab-panel">
            <div className="drives-section">
              <div className="drive-add-section">
                <h3><FaLink /> Add Drive Link</h3>
                <div className="drive-controls">
                  <select 
                    value={selectedDriveType} 
                    onChange={(e) => setSelectedDriveType(e.target.value)}
                    className="drive-type-select"
                  >
                    <option value="">Select Link Type</option>
                    {driveLinkTypes.map(type => (
                      <option key={type.key} value={type.key}>{type.label}</option>
                    ))}
                  </select>
                  <input 
                    type="text" 
                    placeholder="Enter link name/description (e.g., 'Q1 2026 Reports')"
                    value={driveLinkName}
                    onChange={(e) => setDriveLinkName(e.target.value)}
                    className="drive-input"
                  />
                  <input 
                    type="url" 
                    placeholder="Enter Google Drive link"
                    value={driveLink}
                    onChange={(e) => setDriveLink(e.target.value)}
                    className="drive-input"
                  />
                  <button 
                    onClick={handleSaveDriveLink}
                    disabled={!selectedDriveType || !driveLink || !driveLinkName.trim() || savingDriveLink}
                    className="btn-save"
                  >
                    {savingDriveLink ? (
                      <><FaSpinner className="spin" /> Saving...</>
                    ) : (
                      <><FaLink /> Save Link</>
                    )}
                  </button>
                </div>
              </div>

              <div className="drives-grid">
                {driveLinkTypes.map(type => {
                  const links = employee.driveLinks?.[type.key];
                  const linksArray = Array.isArray(links) ? links : (links ? [{ url: links, name: type.label, id: `${type.key}_old` }] : []); // Handle both old and new format
                  
                  return (
                    <div key={type.key} className="drive-card">
                      <div className="drive-header">
                        <FaLink className="drive-icon" />
                        <h4>{type.label}</h4>
                        {linksArray.length > 0 && (
                          <span className="doc-count">{linksArray.length}</span>
                        )}
                      </div>
                      {linksArray.length > 0 ? (
                        <div className="documents-list">
                          {linksArray.map((link, index) => (
                            <div key={link.id || index} className="document-item">
                              <div className="document-info-row">
                                <div className="doc-filename-row">
                                  <FaLink style={{ fontSize: '16px', color: '#2196f3', flexShrink: 0 }} />
                                  <span className="doc-name" title={link.name}>{link.name}</span>
                                </div>
                                <div className="document-actions-inline">
                                  <a 
                                    href={link.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="btn-icon"
                                    title="Open link"
                                  >
                                    <FaLink />
                                  </a>
                                  {userData?.role === 'superadmin' && (
                                    <button 
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🖱️ Delete button clicked!', { type: type.key, linkId: link.id });
                                        handleDeleteDriveLink(type.key, link.id);
                                      }}
                                      className="btn-icon btn-icon-delete"
                                      title="Delete link"
                                      type="button"
                                      disabled={deletingDriveLink === link.id}
                                    >
                                      {deletingDriveLink === link.id ? (
                                        <FaSpinner className="spin" />
                                      ) : (
                                        <FaTrash />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-link">No links added</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Salary Slip Tab */}
        {activeTab === 'salary' && (
          <div className="tab-panel">
            <div className="salary-slip-section">
              <div className="info-card salary-history-card" style={{ maxWidth: '100%' }}>
                <h3><FaCreditCard /> Salary History</h3>
                {employee.salaryHistory && Object.keys(employee.salaryHistory).length > 0 ? (
                  <>
                    <div className="salary-history-list">
                      {Object.entries(employee.salaryHistory)
                        .sort((a, b) => new Date(b[1].payDate) - new Date(a[1].payDate))
                        .map(([month, data]) => (
                          <div key={month} className="salary-history-item">
                            <div className="salary-month">{month.replace(/_/g, '/')}</div>
                            <div className="salary-details">
                              <span>Gross: ₹{data.grossSalary?.toLocaleString()}</span>
                              <span>Net: ₹{data.netSalary?.toLocaleString()}</span>
                              <span>Deductions: ₹{data.totalDeductions?.toLocaleString()}</span>
                              <span className="salary-date">Paid: {data.payDate}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                    {employee.lastSalaryUpdateMonth && (
                      <div className="info-row">
                        <label>Last Salary Update</label>
                        <span>{employee.lastSalaryUpdateMonth} on {employee.lastSalaryUpdateDate}</span>
                      </div>
                    )}
                    <div className="info-row" style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                      <label>Total Salary Slips</label>
                      <span>{Object.keys(employee.salaryHistory).length}</span>
                    </div>
                  </>
                ) : (
                  <div className="empty-state">
                    <FaCreditCard className="empty-icon" />
                    <p className="empty-message">No salary slips created yet</p>
                    <p className="empty-hint">
                      💡 Salary history will automatically appear here when admin creates salary slips in the Salary page.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Experience Letter Tab */}
        {activeTab === 'experience' && (
          <div className="tab-panel">
            <div className="experience-letter-section">
              {employee.status === 'resigned' && employee.hasExperienceLetter ? (
                <div className="info-card experience-letter-card" style={{ maxWidth: '100%' }}>
                  <h3><FaCertificate /> Experience Letter</h3>
                  <div className="info-row">
                    <label>Status</label>
                    <span className="badge badge-success">
                      <FaCheckCircle /> Available
                    </span>
                  </div>
                  <div className="info-row">
                    <label>Employee Status</label>
                    <span className="badge badge-status resigned">
                      <FaTimesCircle /> Resigned
                    </span>
                  </div>
                  <div className="info-row">
                    <label>Experience Record ID</label>
                    <span>{employee.experienceRecordId || employee.employeeId}</span>
                  </div>
                  <div className="info-row">
                    <label>Leaving Date</label>
                    <span>{employee.leavingDate}</span>
                  </div>
                  <div className="info-row">
                    <label>Tenure</label>
                    <span>
                      {employee.joiningDate && employee.leavingDate && (() => {
                        const start = new Date(employee.joiningDate);
                        const end = new Date(employee.leavingDate);
                        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
                        const years = Math.floor(months / 12);
                        const remainingMonths = months % 12;
                        return `${years > 0 ? `${years} year${years > 1 ? 's' : ''} ` : ''}${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
                      })()}
                    </span>
                  </div>
                  <button 
                    className="btn-primary view-experience-btn"
                    onClick={() => {
                      navigate('/dashboard/experience-records');
                      toast.success(`Navigate to Experience Records and search for ${employee.name}`);
                    }}
                  >
                    <FaFileAlt /> View Experience Letter
                  </button>
                </div>
              ) : employee.status === 'active' ? (
                <div className="info-card" style={{ maxWidth: '100%', background: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)', color: 'white' }}>
                  <h3 style={{ color: 'white', borderBottomColor: 'rgba(255,255,255,0.3)' }}><FaCheckCircle /> Currently Active Employee</h3>
                  <div className="empty-state">
                    <FaCheckCircle className="empty-icon" style={{ color: 'rgba(255,255,255,0.9)' }} />
                    <p className="empty-message">This employee is currently active</p>
                    <p className="empty-hint" style={{ background: 'rgba(255,255,255,0.15)' }}>
                      💡 Experience letter will be generated automatically when this employee resigns or leaves the company.
                    </p>
                  </div>
                  <div className="info-row" style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                    <label style={{ color: 'rgba(255,255,255,0.9)' }}>Joining Date</label>
                    <span style={{ color: 'rgba(255,255,255,0.95)' }}>{employee.joiningDate || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <label style={{ color: 'rgba(255,255,255,0.9)' }}>Tenure So Far</label>
                    <span style={{ color: 'rgba(255,255,255,0.95)' }}>
                      {employee.joiningDate && (() => {
                        const start = new Date(employee.joiningDate);
                        const today = new Date();
                        const months = (today.getFullYear() - start.getFullYear()) * 12 + (today.getMonth() - start.getMonth());
                        const years = Math.floor(months / 12);
                        const remainingMonths = months % 12;
                        return `${years > 0 ? `${years} year${years > 1 ? 's' : ''} ` : ''}${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
                      })()}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="info-card" style={{ maxWidth: '100%' }}>
                  <h3><FaCertificate /> Experience Letter</h3>
                  <div className="empty-state">
                    <FaTimesCircle className="empty-icon" />
                    <p className="empty-message">No experience letter available</p>
                    <p className="empty-hint">
                      Experience letter information not found for this employee.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Checklist Manager Tab */}
        {activeTab === 'checklist-manager' && (
          <div className="tab-panel">
            <ChecklistTemplateManager />
          </div>
        )}

        {/* Notes Tab */}
        {activeTab === 'notes' && (
          <div className="tab-panel">
            <div className="notes-section">
              <div className="add-note-section">
                <h3><FaStickyNote /> Add Internal Note</h3>
                <textarea 
                  placeholder="Enter your note here..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="note-textarea"
                  rows="4"
                />
                <button onClick={handleAddNote} className="btn-add-note">
                  Add Note
                </button>
              </div>

              <div className="notes-list">
                <h3>All Notes ({employee.notes?.length || 0})</h3>
                {employee.notes && employee.notes.length > 0 ? (
                  employee.notes.map((note, index) => (
                    <div key={index} className="note-card">
                      <div className="note-header">
                        <span className="note-author">{note.addedByName}</span>
                        <span className="note-date">
                          {new Date(note.addedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' (IST)'}
                        </span>
                      </div>
                      <p className="note-text">{note.text}</p>
                    </div>
                  ))
                ) : (
                  <p className="no-notes">No notes added yet</p>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>

    {/* Appraisal Modal */}
    {appraisalOpen && (
      <AppraisalModal
        employee={employee}
        onClose={() => setAppraisalOpen(false)}
        onAppraisalDone={() => {
          // Firestore is the single source of truth — refetch the full record
          // rather than patching partial fields client-side.
          fetchEmployee();
          setAppraisalOpen(false);
        }}
        onPromoteDone={() => {
          fetchEmployee();
          setAppraisalOpen(false);
        }}
      />
    )}
  </>
  );
};

export default EmployeeDetailPage;
